import { yupResolver } from "@hookform/resolvers/yup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Button, Grid, Paper, Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { resetPassword, sendPhoneOtp, verifyOtp } from "@ui/Auth/api";
import ClientLogo from "components/app-logo/app-logo.component";
import { StyledTextField } from "components/common/style-components/TextField/MuiTextField.component";
import { isEmpty, toString } from "lodash";
import { AppCommonButton } from "pages/cart-page/cart.style";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { MdVerified } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAppApi } from "store/api-context";
import { useAppAuth } from "store/auth-context";
import { getAuthPath, getMainClient } from "utils/common/common.utils";
import { signupForm } from "utils/forms-constants/forms-constants.util";
import { errorToast, successToast } from "utils/toaster/toaster.util";
import * as yup from "yup";

const schema = yup.object({
  mobile: yup.string().required("mobile number is required").min(10).max(10),
  password: yup
    .string()
    .min(8, "Password should be at least 8 characters")
    .max(20, "Password should not exceed 20 characters")
    .required("Password is required"),
  confirm_password: yup
    .string()
    .min(8, "Password should be at least 8 characters")
    .max(20, "Password should not exceed 20 characters")
    .required("Confirm Password is required")
    .oneOf([yup.ref("password")], "Passwords must match"),
});

const ForgotPassword = () => {
  const mainClient = getMainClient();
  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);
  const [otp, setOtp] = useState();
  const [isMobileOTPsent, setIsMobileOTPsent] = useState(false);

  const { isMobileVerified, setIsMobileVerified } = useAppAuth();

  const navigate = useNavigate();
  const { fetchOverview, state } = useAppApi();

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
    setValue,
    setError,
    reset,
  } = useForm({
    defaultValues: {},
    mode: "all",
    resolver: yupResolver(schema),
  });
  const data = getValues();

  const { isMobileVerifySend, isOTPFilled } = useMemo(() => {
    return {
      isMobileVerifySend:
        !errors[signupForm.mobile]?.message && !isEmpty(data?.mobile),
      isOTPFilled: !isEmpty(data?.otp),
    };
  }, [errors, data]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChangeNumber = async () => {
    setIsMobileVerified(false);
  };

  const sendMobileOTP = async (mobile) => {
    try {
      await sendPhoneOtp(mobile, {
        throwIfUserExist: false,
        client: mainClient?.id,
      });
      setIsMobileOTPsent(true);

      setIsMobileVerified(false);
      successToast("Otp has been sent successfully");
    } catch (err) {
      errorToast(err?.response?.data?.message);
    }
  };

  const verifyMobileOTP = async (phone, code) => {
    try {
      await verifyOtp(phone, code);
      successToast("OTP has been verified");
      setIsMobileVerified(true);
      setIsMobileOTPsent(false);
      setValue(signupForm.otp, "");
    } catch (err) {
      setIsMobileVerified(false);
      errorToast(err?.response?.data?.message);
    }
  };

  const handleSendOtp = async () => {
    if (isMobileVerifySend) {
      sendMobileOTP(data?.mobile);
    }
  };

  const handleReset = async () => {
    const isFormValid = await trigger();
    if (!isMobileVerified) {
      return toast("You must validate your mobile number", {
        style: {
          background: "#FF6600",
          padding: "4px",
          color: "#fff",
        },
      });
    }

    try {
      if (isFormValid && isMobileVerified) {
        const data = getValues();
        const userData = {
          phone: data?.mobile, // without +91
          password: data?.password,
          client: mainClient.id,
        };

        await resetPassword(userData);
        setIsMobileVerified(false);
        setIsMobileOTPsent(false);
        toast.success("Password has been reset");
        navigate(getAuthPath());
      }
    } catch (err) {
      setIsMobileVerified(false);
      toast.error(err?.response?.data?.message);
    }
  };

  const handleVerify = (otp) => {
    const otpLength = otp?.length > 4 || otp?.length < 4;

    if (!otpLength && otp !== "") {
      verifyMobileOTP(data?.mobile, otp);
    } else {
      setError("otp", {
        type: "manual",
        message: "OTP must be typed",
      });
    }
  };

  useEffect(() => {
    setIsMobileVerified(false);
  }, []);

  return (
    <Grid
      sx={{
        display: "flex",
        height: "100vh",
        margin: "100 auto",
      }}
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      container
    >
      <Paper
        elevation={2}
        sx={{
          textAlign: "center",
          maxWidth: {
            xs: 300,
            md: 600,
          },
          padding: {
            xs: "10px",
            md: "40px",
          },
        }}
      >
        <div>
          <ClientLogo src={state?.overviews[0]?.logo?.location} />
        </div>

        <form noValidate>
          <Grid
            container
            xs={12}
            alignItems="center"
            sx={{
              paddingLeft: {
                xs: 2,
                md: 6,
              },
              paddingRight: {
                xs: 2,
                md: 6,
              },
            }}
            spacing={{
              xs: 2,
              md: 4,
            }}
          >
            <Grid item xs={12}>
              <Typography className="login_title">Forgot Password</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Grid
                item
                xs={isMobileVerifySend && isMobileVerified === false ? 8 : 12}
                md={isMobileVerifySend && isMobileVerified === false ? 8 : 12}
              >
                <Controller
                  name={signupForm.mobile}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Stack alignItems="flex-start">
                      <StyledTextField
                        fullWidth
                        id={signupForm.mobile}
                        data-testid={signupForm.mobile}
                        label="Mobile"
                        value={value}
                        onChange={onChange}
                        disabled={isMobileVerified}
                        error={!!errors[signupForm.mobile]?.message}
                        helperText={toString(
                          errors[signupForm.mobile]?.message
                        )}
                        InputProps={{
                          endAdornment: isMobileVerified ? (
                            <MdVerified style={{ color: "green", size: 6 }} />
                          ) : null,
                        }}
                      />
                      <Typography
                        onClick={handleChangeNumber}
                        sx={{
                          cursor: "pointer",
                          color: "#A54201",
                          fontWeight: 800,
                          fontSize: "0.8rem",
                          marginTop: "2px",
                        }}
                      >
                        {isMobileVerified
                          ? "Want to change mobile number?"
                          : ""}
                      </Typography>
                    </Stack>
                  )}
                />
              </Grid>
              {isMobileVerifySend && isMobileVerified === false ? (
                <Grid item xs={4}>
                  <Button onClick={handleSendOtp}>
                    {isMobileOTPsent ? "Resend otp" : "Send otp"}
                  </Button>
                </Grid>
              ) : null}
            </Grid>
            {isMobileOTPsent ? (
              <Grid
                item
                xs={12}
                md={12}
                container
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Grid item xs={8}>
                  <Controller
                    name={signupForm.otp}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <StyledTextField
                        fullWidth
                        required
                        id={signupForm.otp}
                        data-testid={signupForm.otp}
                        label="OTP"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e?.target?.value);
                        }}
                        error={!!errors[signupForm.otp]?.message}
                        helperText={toString(errors[signupForm.otp]?.message)}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button onClick={() => handleVerify(otp)}>Verify otp</Button>
                </Grid>
              </Grid>
            ) : null}
            <Grid item xs={12} md={12}>
              <Controller
                name="password"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <StyledTextField
                    required
                    sx={{ width: "100%" }}
                    id={signupForm.password}
                    data-testid={signupForm.password}
                    label="Password"
                    value={value}
                    onChange={onChange}
                    InputProps={{
                      type: showPassword ? "text" : "password",
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={!!errors[signupForm.password]?.message}
                    helperText={errors[signupForm.password]?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Controller
                name="confirm_password"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <StyledTextField
                    required
                    sx={{ width: "100%" }}
                    id={signupForm.confirm_password}
                    data-testid={signupForm.confirm_password}
                    label="Confirm Password"
                    value={value}
                    onChange={onChange}
                    InputProps={{
                      type: confirmShowPassword ? "text" : "password",
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() =>
                              setConfirmShowPassword((show) => !show)
                            }
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {confirmShowPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={!!errors[signupForm.confirm_password]?.message}
                    helperText={errors[signupForm.confirm_password]?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <AppCommonButton
                className="font-600"
                variant="contained"
                margin="unset"
                marginTop="0px"
                extraStyles={{
                  width: "100%",
                }}
                type="submit"
                onClick={handleReset}
              >
                Reset
              </AppCommonButton>
            </Grid>

            <Grid item xs={12}>
              <Typography
                onClick={() => navigate(getAuthPath())}
                style={{
                  cursor: "pointer",
                }}
              >
                Back to login
              </Typography>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Grid>
  );
};

export default ForgotPassword;
